import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Autocomplete,
  Chip,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { caseService, Case } from '../services/caseService';
import { projectAPI, caseAPI } from '../services/api';
import { firmService, Firm } from '../services/firmService';
import { useAuth } from '../contexts/AuthContext';

const statusOptions = ['open', 'in_progress', 'completed', 'closed'];

const FILTER_DEBOUNCE_MS = 320;

function userCanGlobalCaseTracker(user: { roles?: Array<{ slug: string }> } | null): boolean {
  if (!user?.roles) return false;
  return user.roles.some((r) => ['pmp_admin', 'site_admin'].includes(r.slug));
}

const Cases: React.FC = () => {
  const { firmId } = useParams<{ firmId?: string }>();
  const navigate = useNavigate();
  const { user, firmAccess, loading: authLoading } = useAuth();
  const downloadOnly = firmAccess?.mode === 'download_only';

  const isGlobalMode = !firmId && userCanGlobalCaseTracker(user);

  const [loading, setLoading] = useState<boolean>(false);
  const [cases, setCases] = useState<Case[]>([]);
  const [projects, setProjects] = useState<{ id: number; name: string }[]>([]);
  const [firmName, setFirmName] = useState<string>('');
  const [firms, setFirms] = useState<Firm[]>([]);

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [fileNumber, setFileNumber] = useState('');
  const [clientReferenceId, setClientReferenceId] = useState('');
  const [debouncedTitle, setDebouncedTitle] = useState('');
  const [debouncedSubtitle, setDebouncedSubtitle] = useState('');
  const [debouncedFileNumber, setDebouncedFileNumber] = useState('');
  const [debouncedClientReferenceId, setDebouncedClientReferenceId] = useState('');
  const [contentSearch, setContentSearch] = useState('');
  const [debouncedContentSearch, setDebouncedContentSearch] = useState('');
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedClientFirmId, setSelectedClientFirmId] = useState<number | null>(null);

  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(15);
  const [total, setTotal] = useState<number>(0);
  const [lastPage, setLastPage] = useState<number>(1);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedTitle(title), FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [title]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSubtitle(subtitle), FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [subtitle]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedFileNumber(fileNumber), FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [fileNumber]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedClientReferenceId(clientReferenceId), FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [clientReferenceId]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedContentSearch(contentSearch), FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [contentSearch]);

  useEffect(() => {
    if (!isGlobalMode) return;
    const loadFirms = async () => {
      try {
        const res = await firmService.getAllFirms({ per_page: 500 });
        const list = res.data?.data ?? [];
        setFirms(Array.isArray(list) ? list : []);
      } catch {
        setFirms([]);
      }
    };
    loadFirms();
  }, [isGlobalMode]);

  useEffect(() => {
    const loadProjects = async () => {
      if (firmId) {
        try {
          const projectsRes = await projectAPI.getAll({ firm_id: firmId, per_page: 200 });
          const projList = projectsRes.data.data?.data || projectsRes.data.data || [];
          setProjects(projList.map((p: any) => ({ id: p.id, name: p.name })));
        } catch {
          setProjects([]);
        }
        return;
      }
      if (isGlobalMode && selectedClientFirmId) {
        try {
          const projectsRes = await projectAPI.getAll({
            firm_id: String(selectedClientFirmId),
            per_page: 200,
          });
          const projList = projectsRes.data.data?.data || projectsRes.data.data || [];
          setProjects(projList.map((p: any) => ({ id: p.id, name: p.name })));
        } catch {
          setProjects([]);
        }
        return;
      }
      setProjects([]);
    };
    loadProjects();
  }, [firmId, isGlobalMode, selectedClientFirmId]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedTitle,
    debouncedSubtitle,
    debouncedFileNumber,
    debouncedClientReferenceId,
    selectedProjectIds,
    selectedStatuses,
    selectedClientFirmId,
    debouncedContentSearch,
    firmId,
  ]);

  const filterParamsBase = useMemo(() => {
    const filterParams: Record<string, unknown> = {
      page,
      per_page: perPage,
    };
    if (debouncedTitle) filterParams.title = debouncedTitle;
    if (debouncedSubtitle) filterParams.subtitle = debouncedSubtitle;
    if (debouncedFileNumber) filterParams.file_number = debouncedFileNumber;
    if (debouncedClientReferenceId) filterParams.client_reference_id = debouncedClientReferenceId;
    if (debouncedContentSearch) filterParams.search = debouncedContentSearch;
    if (selectedProjectIds.length > 0) filterParams.project_id = selectedProjectIds;
    if (selectedStatuses.length > 0) filterParams.statuses = selectedStatuses;
    return filterParams;
  }, [
    page,
    perPage,
    debouncedTitle,
    debouncedSubtitle,
    debouncedFileNumber,
    debouncedClientReferenceId,
    debouncedContentSearch,
    selectedProjectIds,
    selectedStatuses,
  ]);

  useEffect(() => {
    if (authLoading) return;
    if (!firmId && !userCanGlobalCaseTracker(user)) return;

    const load = async () => {
      setLoading(true);
      try {
        let casesRes;

        if (isGlobalMode) {
          const params: Record<string, unknown> = { ...filterParamsBase };
          if (selectedClientFirmId) {
            params.firm_id = selectedClientFirmId;
          }
          casesRes = await caseService.getAllCases(params);
        } else if (firmId) {
          casesRes = await caseService.getFirmCases(parseInt(firmId, 10), filterParamsBase);
          try {
            const firmRes = await firmService.getFirmById(parseInt(firmId, 10));
            setFirmName(firmRes.data.name);
          } catch {
            setFirmName('');
          }
        } else {
          setLoading(false);
          return;
        }

        if (casesRes.data && casesRes.data.data) {
          setCases(casesRes.data.data);
          setTotal(casesRes.data.total || 0);
          setLastPage(casesRes.data.last_page || 1);
        } else {
          const casesList = casesRes.data?.data || casesRes.data || [];
          setCases(Array.isArray(casesList) ? casesList : []);
          setTotal(Array.isArray(casesList) ? casesList.length : 0);
          setLastPage(1);
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authLoading, user, firmId, isGlobalMode, filterParamsBase, selectedClientFirmId]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePerPageChange = (event: any) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params: Record<string, unknown> = { ...filterParamsBase };
      delete params.page;
      delete params.per_page;
      if (firmId) params.firm_id = parseInt(firmId, 10);
      if (isGlobalMode && selectedClientFirmId) params.firm_id = selectedClientFirmId;

      const response = await caseAPI.exportExcel(params);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cases_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // silently fail
    } finally {
      setExporting(false);
    }
  };

  const navigateToCase = (c: Case) => {
    const fid = c.project?.firm_id ?? (firmId ? parseInt(firmId, 10) : undefined);
    if (fid) {
      navigate(`/firms/${fid}/cases/${c.id}`);
    }
  };

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!firmId && !userCanGlobalCaseTracker(user)) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Cases
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Select a firm to view Case Tracker.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const selectedFirmOption = firms.find((f) => f.id === selectedClientFirmId) ?? null;

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/firms');
          }}
          sx={{ textDecoration: 'none' }}
        >
          Firms
        </Link>
        {!isGlobalMode && firmId ? (
          <>
            <Link
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate(`/firms/${firmId}`);
              }}
              sx={{ textDecoration: 'none' }}
            >
              {firmName || `Firm #${firmId}`}
            </Link>
            <Typography color="text.primary">Case Tracker</Typography>
          </>
        ) : (
          <Typography color="text.primary">Global Case Tracker</Typography>
        )}
      </Breadcrumbs>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          {isGlobalMode ? 'Global Case Tracker' : 'Case Tracker'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {firmId && !downloadOnly && (
            <Button variant="contained" onClick={() => navigate(`/firms/${firmId}/cases/bulk`)}>
              Create cases
            </Button>
          )}
          {!downloadOnly && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              disabled={exporting || loading}
            >
              {exporting ? 'Exporting...' : 'Export Excel'}
            </Button>
          )}
        </Box>
      </Box>
      {isGlobalMode && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search cases across all firms. Newest cases appear first. Optionally narrow by firm or project.
        </Typography>
      )}

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} flexWrap="wrap" gap={2}>
          {isGlobalMode && (
            <Box flex={{ xs: '1 1 100%', sm: '1 1 280px' }} minWidth={{ xs: '100%', sm: 260 }}>
              <Autocomplete
                size="small"
                options={firms}
                getOptionLabel={(o) => o.name}
                value={selectedFirmOption}
                onChange={(_e, val) => {
                  setSelectedClientFirmId(val?.id ?? null);
                  setSelectedProjectIds([]);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Firm (optional)" placeholder="All firms" />
                )}
              />
            </Box>
          )}
          <Box flex={{ xs: '1 1 100%', sm: '1 1 220px' }}>
            <TextField label="Title" fullWidth size="small" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Box>
          <Box flex={{ xs: '1 1 100%', sm: '1 1 220px' }}>
            <TextField
              label="Sub Title"
              fullWidth
              size="small"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
            />
          </Box>
          <Box flex={{ xs: '1 1 100%', sm: '1 1 260px' }} minWidth={{ xs: '100%', sm: 240 }}>
            <Autocomplete
              multiple
              size="small"
              options={projects}
              getOptionLabel={(o) => o.name}
              value={projects.filter((p) => selectedProjectIds.includes(p.id))}
              onChange={(_e, vals: { id: number; name: string }[]) => setSelectedProjectIds(vals.map((v) => v.id))}
              disabled={isGlobalMode && !selectedClientFirmId && projects.length === 0}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option.name} {...getTagProps({ index })} key={option.id} size="small" />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Projects"
                  placeholder={isGlobalMode && !selectedClientFirmId ? 'Select a firm first' : 'Choose some options'}
                />
              )}
            />
          </Box>
          <Box flex={{ xs: '1 1 100%', sm: '1 1 220px' }} minWidth={{ xs: '100%', sm: 240 }}>
            <Autocomplete
              multiple
              size="small"
              options={statusOptions}
              getOptionLabel={(o) => o.replace('_', ' ')}
              value={selectedStatuses}
              onChange={(_e, vals: string[]) => setSelectedStatuses(vals)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option.replace('_', ' ')} {...getTagProps({ index })} key={option} size="small" />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Status" placeholder="Choose some options" />}
            />
          </Box>
          <Box flex={{ xs: '1 1 100%', sm: '1 1 220px' }}>
            <TextField
              label="Client ID"
              fullWidth
              size="small"
              value={clientReferenceId}
              onChange={(e) => setClientReferenceId(e.target.value)}
              helperText="ID the client uses in their system"
            />
          </Box>
          <Box flex={{ xs: '1 1 100%', sm: '1 1 220px' }}>
            <TextField
              label="File Number"
              fullWidth
              size="small"
              value={fileNumber}
              onChange={(e) => setFileNumber(e.target.value)}
            />
          </Box>
          <Box flex={{ xs: '1 1 100%', sm: '1 1 280px' }}>
            <TextField
              label="Content Search"
              fullWidth
              size="small"
              value={contentSearch}
              onChange={(e) => setContentSearch(e.target.value)}
              placeholder="Title, client ID, description, comments..."
              helperText="Searches case text, client ID, and comments"
            />
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Table size="small" sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>ID</TableCell>
                {isGlobalMode && (
                  <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Firm</TableCell>
                )}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Title</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Sub Title</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>Project</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Created By</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Client ID</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>File Number</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cases.map((c) => {
                const canOpen = Boolean(c.project?.firm_id ?? firmId);
                return (
                  <TableRow key={c.id} hover>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>#{c.id}</TableCell>
                    {isGlobalMode && (
                      <TableCell
                        sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {c.project?.firm?.name ?? '—'}
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography
                        sx={{
                          color: canOpen ? 'primary.main' : 'text.disabled',
                          cursor: canOpen ? 'pointer' : 'default',
                          textDecoration: canOpen ? 'underline' : 'none',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                        onClick={() => canOpen && navigateToCase(c)}
                      >
                        {c.title}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {c.subtitle || '—'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {c.project?.name || '—'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {c.creator?.name || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={String(c.status).replace('_', ' ').toUpperCase()} />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {c.client_reference_id || '—'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {c.file_number || '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
              {cases.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={isGlobalMode ? 9 : 8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No cases match your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && total > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Per Page</InputLabel>
              <Select value={perPage} label="Per Page" onChange={handlePerPageChange}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={15}>15</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, total)} of {total} cases
            </Typography>
          </Box>
          <Pagination
            count={lastPage}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default Cases;
